import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Sydney CBD — safe fallback until the user's actual coords are known.
const DEFAULT_CENTER = [-33.8688, 151.2093];
const DEFAULT_ZOOM = 13;
const MAX_ZOOM = 19;
const SELECTED_MIN_ZOOM = 14;
const INVALIDATE_SIZE_DELAY_MS = 100;

/**
 * Map wrapper using vanilla Leaflet (React 19 compatible).
 * Props:
 *  - openings: array of { id, lat, lng, service_type, title, status }
 *  - center: [lat, lng]
 *  - onSelect: (opening) => void
 *  - selectedId: string | null
 */
export default function MapView({
  openings = [],
  center = DEFAULT_CENTER,
  onSelect,
  selectedId,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});

  // Init map once on mount. `center` is only used for the initial view;
  // subsequent centering is handled by the selectedId effect below.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      attributionControl: true,
    });
    // OpenStreetMap standard tiles — free, no API key, no commercial-use gate.
    // Chosen over CartoDB Positron (previous provider) which started showing
    // "API KEY REQUIRED" watermarks in Feb 2026. OSM tile policy requires a
    // valid User-Agent and reasonable usage — for a small-business booking
    // marketplace this comfortably fits within their acceptable-use limits.
    L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: MAX_ZOOM,
        subdomains: "abc",
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    ).addTo(map);
    mapRef.current = map;

    // Fix leaflet resize after mount. Track the timer so unmount can cancel
    // it — otherwise navigating away before the timer fires calls
    // invalidateSize() on a torn-down map (StrictMode double-invoke or route
    // change triggers `Cannot read properties of undefined ('_leaflet_pos')`).
    const invalidateTimer = setTimeout(
      () => map.invalidateSize(),
      INVALIDATE_SIZE_DELAY_MS,
    );

    return () => {
      clearTimeout(invalidateTimer);
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update markers when openings change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove markers not in new list
    const newIds = new Set(openings.map((o) => o.id));
    Object.keys(markersRef.current).forEach((id) => {
      if (!newIds.has(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });

    openings.forEach((o) => {
      const icon = L.divIcon({
        className: "",
        html: `<div class="sf-pin ${o.status === "filled" ? "filled" : ""}${o.is_platinum ? " platinum" : ""}" data-testid="map-pin-${o.id}">${initials(o.service_type)}</div>`,
        iconSize: [32, 40],
        iconAnchor: [16, 40],
      });

      if (markersRef.current[o.id]) {
        markersRef.current[o.id].setLatLng([o.lat, o.lng]);
        markersRef.current[o.id].setIcon(icon);
      } else {
        const m = L.marker([o.lat, o.lng], { icon })
          .addTo(map)
          .on("click", () => onSelect && onSelect(o));
        markersRef.current[o.id] = m;
      }
    });
  }, [openings, onSelect]);

  // Pan to selected
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const o = openings.find((x) => x.id === selectedId);
    if (o) map.setView([o.lat, o.lng], Math.max(map.getZoom(), SELECTED_MIN_ZOOM));
  }, [selectedId, openings]);

  // Recenter when `center` prop changes (e.g. business's saved address loads
  // after mount). We only re-pan when no pin is selected, so an active
  // selection doesn't get overridden by a background prop change.
  const centerLat = Array.isArray(center) ? center[0] : null;
  const centerLng = Array.isArray(center) ? center[1] : null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || centerLat == null || centerLng == null || selectedId) return;
    map.setView([centerLat, centerLng], map.getZoom());
  }, [centerLat, centerLng, selectedId]);

  return <div ref={containerRef} data-testid="map-container" className="h-full w-full" />;
}

function initials(s = "") {
  const parts = s.split(" ").filter(Boolean);
  if (parts.length === 0) return "•";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
